import React from "react";
import {
    Box, Grid, Paper, Typography, Chip, Tooltip, IconButton,
    CircularProgress, TablePagination,
} from "@mui/material";
import WorkIcon from "@mui/icons-material/Work";
import VisibilityIcon from "@mui/icons-material/Visibility";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import PlayArrowIcon from "@mui/icons-material/PlayArrow";
import ScheduleIcon from "@mui/icons-material/Schedule";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import ErrorIcon from "@mui/icons-material/Error";

import { SECTION_COLORS, ROWS_PER_PAGE } from "../../constants";
import EmptyState from "../shared/EmptyState";
import ErrorState from "../shared/ErrorState";
import StatusChip from "../shared/StatusChip";

const STATUS_STYLE = {
    SUCCESS: { bgcolor: "#f0fdf4", color: "#16a34a", icon: <CheckCircleIcon sx={{ fontSize: 12 }} /> },
    FAILED: { bgcolor: "#fef2f2", color: "#DC2626", icon: <ErrorIcon sx={{ fontSize: 12 }} /> },
};

const fmtDate = (v) => v ? new Date(v).toLocaleString("fr-FR", { dateStyle: "short", timeStyle: "short" }) : "–";

const InternalJobsTab = ({
    internalJobsApi,
    pages,
    handlePageChange,
    onView,
    onEdit,
    onDelete,
    onRun,
    tabIndex = 5,
}) => {
    if (internalJobsApi.error) return <ErrorState error={internalJobsApi.error} onRetry={() => internalJobsApi.refetch()} />;
    if (internalJobsApi.loading) return <Box py={3} display="flex" justifyContent="center"><CircularProgress size={28} /></Box>;
    if (!Array.isArray(internalJobsApi.data) || internalJobsApi.data.length === 0)
        return <EmptyState message="Aucun job interne configuré" color={SECTION_COLORS.internalJobs.main} />;

    const page = pages[tabIndex] ?? 0;
    const paginated = internalJobsApi.data.slice(page * ROWS_PER_PAGE, page * ROWS_PER_PAGE + ROWS_PER_PAGE);

    return (
        <Box>
            <Grid container spacing={2}>
                {paginated.map((job) => {
                    const lastStatus = job.last_status;
                    const statusStyle = STATUS_STYLE[lastStatus] || null;

                    return (
                        <Grid item xs={12} sm={6} md={4} key={job.id}>
                            <Paper elevation={0} sx={{
                                p: 2.5, borderRadius: 3, position: "relative", overflow: "hidden",
                                border: `1px solid ${SECTION_COLORS.internalJobs.main}15`,
                                background: `linear-gradient(135deg, ${SECTION_COLORS.internalJobs.main}06 0%, transparent 100%)`,
                                boxShadow: "0 8px 24px rgba(15, 23, 42, 0.10), 0 2px 8px rgba(15, 23, 42, 0.06)",
                                transition: "all 0.25s ease",
                                "&:hover": {
                                    transform: "translateY(-2px)",
                                    boxShadow: "0 14px 30px rgba(15, 23, 42, 0.14), 0 6px 14px rgba(15, 23, 42, 0.10)",
                                    borderColor: `${SECTION_COLORS.internalJobs.main}30`,
                                },
                            }}>
                                {/* transaction_type badge */}
                                <Tooltip title={`Event type : ${job.transaction_type}`} placement="left">
                                    <Chip label={job.transaction_type} size="small"
                                        sx={{
                                            position: "absolute", top: 10, right: 10,
                                            fontWeight: 700, fontSize: 10,
                                            bgcolor: "#f0f9ff", color: "#0369a1", maxWidth: 120,
                                        }} />
                                </Tooltip>

                                {/* Header */}
                                <Box display="flex" alignItems="center" gap={1} mb={0.5} mr={14}>
                                    <WorkIcon sx={{ color: SECTION_COLORS.internalJobs.main, fontSize: 20, flexShrink: 0 }} />
                                    <Typography fontWeight={700} fontSize={14} fontFamily="Montserrat, sans-serif" color="#1a1a2e" noWrap>
                                        {job.job_key}
                                    </Typography>
                                </Box>

                                {/* Brand */}
                                {job.brand && (
                                    <Chip label={job.brand} size="small"
                                        sx={{ mb: 1, fontSize: 10, fontWeight: 600, bgcolor: "#f0fdf4", color: "#15803d" }} />
                                )}

                                {/* Schedule */}
                                {job.schedule?.cron && (
                                    <Box display="flex" alignItems="center" gap={0.5} mb={1}>
                                        <ScheduleIcon sx={{ fontSize: 13, color: "text.secondary" }} />
                                        <Typography fontSize={11} color="text.secondary" fontFamily="monospace">
                                            {job.schedule.cron}
                                            {job.schedule.timezone && job.schedule.timezone !== "UTC" && ` (${job.schedule.timezone})`}
                                        </Typography>
                                    </Box>
                                )}

                                {/* Next run */}
                                <Box display="flex" gap={1} mb={1.5} flexWrap="wrap">
                                    {job.next_run_at && (
                                        <Chip label={`Prochain : ${fmtDate(job.next_run_at)}`} size="small"
                                            sx={{ fontSize: 10, bgcolor: "#f0f9ff", color: "#0369a1" }} />
                                    )}
                                    {job.last_run_at && (
                                        <Chip label={`Dernier : ${fmtDate(job.last_run_at)}`} size="small"
                                            sx={{ fontSize: 10, bgcolor: "#fafbfd", color: "#64748b" }} />
                                    )}
                                </Box>

                                {/* Last status */}
                                {lastStatus && statusStyle && (
                                    <Box display="flex" alignItems="center" gap={0.5} mb={1.5}
                                        sx={{ borderRadius: 1.5, px: 1, py: 0.5, bgcolor: statusStyle.bgcolor, width: "fit-content" }}>
                                        {statusStyle.icon}
                                        <Typography fontSize={11} fontWeight={600} color={statusStyle.color}>
                                            {lastStatus === "SUCCESS" ? "Succès" : "Échec"}
                                        </Typography>
                                    </Box>
                                )}

                                {/* Footer */}
                                <Box display="flex" alignItems="center" justifyContent="space-between">
                                    <StatusChip active={job.active} />
                                    <Box display="flex" gap={0.5}>
                                        <Tooltip title="Voir le détail">
                                            <IconButton size="small" onClick={() => onView(job)}
                                                sx={{
                                                    color: SECTION_COLORS.internalJobs.main,
                                                    bgcolor: `${SECTION_COLORS.internalJobs.main}10`,
                                                    "&:hover": { bgcolor: `${SECTION_COLORS.internalJobs.main}20` }, width: 30, height: 30
                                                }}>
                                                <VisibilityIcon sx={{ fontSize: 16 }} />
                                            </IconButton>
                                        </Tooltip>
                                        <Tooltip title="Exécuter maintenant">
                                            <span>
                                                <IconButton size="small" onClick={() => onRun(job)} disabled={!job.active}
                                                    sx={{ color: "#16a34a", bgcolor: "#f0fdf4", "&:hover": { bgcolor: "#dcfce7" }, width: 30, height: 30 }}>
                                                    <PlayArrowIcon sx={{ fontSize: 16 }} />
                                                </IconButton>
                                            </span>
                                        </Tooltip>
                                        <Tooltip title="Modifier">
                                            <IconButton size="small" onClick={() => onEdit(job)}
                                                sx={{ color: "#6366f1", bgcolor: "#eef2ff", "&:hover": { bgcolor: "#e0e7ff" }, width: 30, height: 30 }}>
                                                <EditIcon sx={{ fontSize: 16 }} />
                                            </IconButton>
                                        </Tooltip>
                                        <Tooltip title="Supprimer">
                                            <IconButton size="small" onClick={() => onDelete(job)}
                                                sx={{ color: "#DC2626", bgcolor: "#fef2f2", "&:hover": { bgcolor: "#fee2e2" }, width: 30, height: 30 }}>
                                                <DeleteIcon sx={{ fontSize: 16 }} />
                                            </IconButton>
                                        </Tooltip>
                                    </Box>
                                </Box>
                            </Paper>
                        </Grid>
                    );
                })}
            </Grid>

            {internalJobsApi.data.length > ROWS_PER_PAGE && (
                <TablePagination component="div" count={internalJobsApi.data.length} page={page}
                    onPageChange={handlePageChange(tabIndex)} rowsPerPage={ROWS_PER_PAGE} rowsPerPageOptions={[ROWS_PER_PAGE]}
                    sx={{ mt: 1, borderTop: "1px solid #f0f0f0" }} />
            )}
        </Box>
    );
};

export default InternalJobsTab;
